/**
 * Minimal api client: JSON in, zod-parsed JSON out, 10 s timeout on every call, bearer JWT
 * when a session exists. Phone numbers never appear in query strings built here.
 */
import type { z } from 'zod';
import { TimeoutError } from '../keepalive/timers.js';

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  fetchFn?: FetchLike;
  timeoutMs?: number;
  getJwt?: () => string | null;
}

export class ApiClient {
  private readonly fetchFn: FetchLike;
  private readonly timeoutMs: number;
  readonly baseUrl: string;

  constructor(private readonly opts: ApiClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, '');
    this.fetchFn = opts.fetchFn ?? ((input, init) => fetch(input, init));
    this.timeoutMs = opts.timeoutMs ?? 10_000;
  }

  async request<T>(method: 'GET' | 'POST' | 'DELETE', path: string, schema: z.ZodType<T>, body?: unknown, extra: { auth?: boolean; credentials?: RequestCredentials } = {}): Promise<T> {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), this.timeoutMs);
    const headers: Record<string, string> = { accept: 'application/json' };
    if (body !== undefined) headers['content-type'] = 'application/json';
    const jwt = extra.auth === false ? null : this.opts.getJwt?.() ?? null;
    if (jwt) headers['authorization'] = `Bearer ${jwt}`;
    let res: Response;
    try {
      res = await this.fetchFn(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: ctl.signal,
        credentials: extra.credentials ?? 'omit',
        cache: 'no-store',
      });
    } catch (e) {
      clearTimeout(timer);
      if (ctl.signal.aborted) throw new TimeoutError(`${method} ${path}`, this.timeoutMs);
      throw new ApiError(0, 'network', e instanceof Error ? e.message : 'network error');
    }
    clearTimeout(timer);
    const text = await res.text();
    let json: unknown = null;
    if (text.length > 0) {
      try {
        json = JSON.parse(text);
      } catch {
        json = null;
      }
    }
    if (!res.ok) {
      const err = (json ?? {}) as { error?: string; code?: string; message?: string };
      throw new ApiError(res.status, err.code ?? err.error ?? `http_${res.status}`, err.message ?? `${method} ${path} -> ${res.status}`);
    }
    const parsed = schema.safeParse(json);
    if (!parsed.success) throw new ApiError(res.status, 'invalid_response', `${method} ${path}: ${parsed.error.message}`);
    return parsed.data;
  }
}
